import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, Typography, Space, Divider } from 'antd';
import { UserAddOutlined, WhatsAppOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Paragraph } = Typography;

interface PlayerForm {
  player_name: string;
}

const PublicAddPlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [playerAdded, setPlayerAdded] = useState(false);
  const [addedPlayerName, setAddedPlayerName] = useState('');

  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

  const handleSubmit = async (values: PlayerForm) => {
    if (!id) {
      message.error('ID del gioco non valido');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${apiEndpoint}/public/games/${id}/players`,
        { player_name: values.player_name }
      );

      setAddedPlayerName(values.player_name);
      setPlayerAdded(true);
      message.success('Giocatore aggiunto con successo!');
      form.resetFields();
    } catch (error: any) {
      console.error('Errore aggiunta giocatore:', error);
      if (error.response?.status === 404) {
        message.error('Gioco non trovato. Verifica il link.');
      } else if (error.response?.status === 400) {
        message.error(error.response?.data?.error?.message || 'Dati non validi');
      } else {
        message.error('Errore durante l\'aggiunta del giocatore');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = () => {
    const currentUrl = window.location.href;
    const text = encodeURIComponent(
      `🎅 Ciao! Sei stato invitato a partecipare al Babbo Natale Segreto!\n\nAggiungi il tuo nome cliccando qui: ${currentUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleAddAnother = () => {
    setPlayerAdded(false);
    setAddedPlayerName('');
  };

  if (playerAdded) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <Card
          style={{ maxWidth: 500, width: '100%' }}
          bordered={false}
        >
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
            <Title level={2}>Benvenuto!</Title>
            <Paragraph style={{ fontSize: 16 }}>
              <strong>{addedPlayerName}</strong> sei stato aggiunto con successo al gioco!
            </Paragraph>

            <Divider />

            <Paragraph>
              Vuoi invitare altri amici? Condividi questo link!
            </Paragraph>

            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<WhatsAppOutlined />}
                onClick={handleShareWhatsApp}
                size="large"
                block
                style={{
                  background: '#25D366',
                  borderColor: '#25D366',
                  height: 'auto',
                  padding: '12px 24px'
                }}
              >
                Condividi su WhatsApp
              </Button>

              <Button
                onClick={handleAddAnother}
                size="large"
                block
              >
                Aggiungi un altro giocatore
              </Button>
            </Space>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card
        style={{ maxWidth: 500, width: '100%' }}
        bordered={false}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2}>🎅 Babbo Natale Segreto</Title>
            <Paragraph style={{ fontSize: 16 }}>
              Sei stato invitato a partecipare! Inserisci la tua email (opzionalmente anche il nome) per unirti al gioco.
            </Paragraph>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              label="Email (e Nome opzionale)"
              name="player_name"
              rules={[
                { required: true, message: 'Inserisci almeno la tua email' },
                {
                  pattern: /\S+@\S+\.\S+/,
                  message: 'Inserisci un indirizzo email valido'
                }
              ]}
              extra="Consigliato: Nome Cognome email@example.com oppure solo: email@example.com"
            >
              <Input
                size="large"
                placeholder="mario@example.com oppure Mario Rossi mario@example.com"
                prefix={<UserAddOutlined />}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                block
              >
                Partecipa al gioco
              </Button>
            </Form.Item>
          </Form>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Paragraph>
              Vuoi invitare altri amici?
            </Paragraph>
            <Button
              icon={<WhatsAppOutlined />}
              onClick={handleShareWhatsApp}
              size="large"
              style={{
                background: '#25D366',
                borderColor: '#25D366',
                color: 'white'
              }}
            >
              Condividi questo link
            </Button>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default PublicAddPlayer;